import React from 'react';
import { useTranslation } from 'react-i18next';
import { isValueDefined } from '../../../Helpers/CleanData.helper';
import { Field, FormikErrors, FormikTouched } from 'formik';
import {
  Button,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '../../../UIKit';
import { IOnboardingData } from '../IOnboardingData.model';
import { OnboardingTermsAndConditions } from './OnboardingTermsAndConditions/OnboardingTermsAndConditions';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Visibility from '@mui/icons-material/Visibility';
import { usePasswordToggler } from '../../../Hooks/usePasswordHidden';
import { makeStyles } from '~/UIKit/Style';

interface IOnboardingDetailsProps {
  isStudioDetailsDone: boolean;
  onCreateProviderAccount: () => void;
  onRedirectToLogin: () => void;
  isAlreadyAProvider: boolean;
  isAlreadyAUser: boolean;
  isPasswordWrong: boolean;
  errors: FormikErrors<IOnboardingData>;
  touched: FormikTouched<IOnboardingData>;
}

const useStyles = makeStyles({
  onboardingDetailsContainer: {
    width: 300,
    margin: 'auto',
  },

  onboardingDetailsTitle: {
    fontSize: '28px',
    fontWeight: 'bold',
    lineHeight: '32px',
    textAlign: 'center',
    overflowWrap: 'break-word',
  },

  onboardingDetailsLabel: {
    fontWeight: 'bold',
    lineHeight: '27px',
    fontSize: '13px',
    marginTop: 30,
  },

  onboardingDetailsInputContainer: {
    marginTop: 5,
  },

  onboardingDetailsInput: {
    width: 300,
    '& >.MuiSelect-selectMenu': {
      display: 'flex',
      alignItems: 'center',
    },
  },

  onboardingDetailsConfirmButton: {
    marginTop: 30,
    width: 300,
    height: 50,
    fontSize: '1rem',
  },
});

export const OnboardingDetails = ({
  isStudioDetailsDone,
  onCreateProviderAccount,
  onRedirectToLogin,
  isAlreadyAProvider,
  isAlreadyAUser,
  isPasswordWrong,
  errors,
  touched,
}: IOnboardingDetailsProps) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { onShowPasswordClick, isPasswordVisible, passwordInputType } = usePasswordToggler();

  return (
    <div className={classes.onboardingDetailsContainer}>
      <Typography className={classes.onboardingDetailsTitle}>
        {t('onboarding.personalDetails.page.title')}
      </Typography>

      <Typography className={classes.onboardingDetailsLabel}>
        {t('onboarding.personalDetails.question.fullName')}
      </Typography>
      <FormControl
        className={classes.onboardingDetailsInputContainer}
        error={touched.firstName && isValueDefined(errors.firstName)}
      >
        <Field
          component={TextField}
          fullWidth
          label={t('app.firstName')}
          placeholder={t('app.firstName')}
          className={classes.onboardingDetailsInput}
          name="firstName"
          disabled={isAlreadyAProvider || isAlreadyAUser}
          error={touched.firstName && isValueDefined(errors.firstName)}
          inputProps={{ maxLength: 50 }}
        />
        <FormHelperText data-testid="firstNameError">
          {touched.firstName && errors.firstName}
        </FormHelperText>
      </FormControl>
      <FormControl
        className={classes.onboardingDetailsInputContainer}
        error={touched.lastName && isValueDefined(errors.lastName)}
      >
        <Field
          component={TextField}
          fullWidth
          label={t('app.lastName')}
          placeholder={t('app.lastName')}
          className={classes.onboardingDetailsInput}
          name="lastName"
          disabled={isAlreadyAProvider || isAlreadyAUser}
          error={touched.lastName && isValueDefined(errors.lastName)}
          inputProps={{ maxLength: 50 }}
        />
        <FormHelperText data-testid="lastNameError">
          {touched.lastName && errors.lastName}
        </FormHelperText>
      </FormControl>

      <Typography className={classes.onboardingDetailsLabel}>
        {t('onboarding.personalDetails.question.email')}
      </Typography>
      <FormControl
        className={classes.onboardingDetailsInputContainer}
        error={
          isAlreadyAProvider || isAlreadyAUser || (touched.email && isValueDefined(errors.email))
        }
      >
        <Field
          component={TextField}
          fullWidth
          label={t('app.email')}
          placeholder={t('app.email')}
          className={classes.onboardingDetailsInput}
          error={
            isAlreadyAProvider || isAlreadyAUser || (touched.email && isValueDefined(errors.email))
          }
          name="email"
          type="email"
        />
        <FormHelperText data-testid="emailError">
          {(isAlreadyAProvider && t('onboarding.personalDetails.loginBelow')) ||
            (touched.email && errors.email)}
        </FormHelperText>
      </FormControl>

      <Typography className={classes.onboardingDetailsLabel}>
        {t('onboarding.personalDetails.question.password')}
      </Typography>
      <FormControl
        className={classes.onboardingDetailsInputContainer}
        error={
          isPasswordWrong || isAlreadyAUser || (touched.password && isValueDefined(errors.password))
        }
      >
        <Field
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  edge="end"
                  onClick={onShowPasswordClick}
                  size="large"
                >
                  {isPasswordVisible ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          component={TextField}
          fullWidth
          label={t('app.password')}
          placeholder={t('app.password')}
          className={classes.onboardingDetailsInput}
          error={isPasswordWrong || (touched.password && isValueDefined(errors.password))}
          name="password"
          type={passwordInputType}
          disabled={isAlreadyAProvider}
        />

        <FormHelperText data-testid="passwordError">
          {(isPasswordWrong && t('userService.invalidCredentials')) ||
            (isAlreadyAUser && t('settings.manager.emailExisting')) ||
            (touched.password && errors.password)}
        </FormHelperText>
      </FormControl>

      <OnboardingTermsAndConditions disabled={isAlreadyAProvider} />

      {isAlreadyAProvider ? (
        <Button
          className={classes.onboardingDetailsConfirmButton}
          onClick={onRedirectToLogin}
          variant="outlined"
          data-testid="loginButton"
        >
          {t('app.login')}
        </Button>
      ) : (
        <Button
          className={classes.onboardingDetailsConfirmButton}
          disabled={!isStudioDetailsDone}
          onClick={onCreateProviderAccount}
          variant="outlined"
          data-testid="registrationButton"
        >
          {t('onboarding.personalDetails.button.continue')}
        </Button>
      )}
    </div>
  );
};
